import { generateTestId } from "@client-shared/lib/util";
import type { LabelPrintBatchDetail } from "@shared/types";

export const setBatchSummaryDialogTestId = (identifier: string): string => generateTestId("BatchSummaryDialog", identifier);

export interface BatchSummaryGrind {
  readonly grindType: string;
  readonly totalUnits: number;
  readonly totalWeight: number;
  readonly fulfilledUnits: number;
  readonly fulfilledWeight: number;
  readonly errorUnits: number;
  readonly errorWeight: number;
}

export interface BatchSummaryProduct {
  readonly title?: string;
  readonly totalUnits: number;
  readonly totalWeight: number;
  readonly fulfilledUnits: number;
  readonly fulfilledWeight: number;
  readonly errorUnits: number;
  readonly errorWeight: number;
  readonly grinds: BatchSummaryGrind[];
}

const ERROR_STATUSES = new Set(["LabelError", "Error"]);

type LineItem = NonNullable<NonNullable<LabelPrintBatchDetail["purchaseOrders"]>[number]["lineItems"]>[number];

const emptyProduct = (title: string | undefined): BatchSummaryProduct => ({
  title,
  totalUnits: 0,
  totalWeight: 0,
  fulfilledUnits: 0,
  fulfilledWeight: 0,
  errorUnits: 0,
  errorWeight: 0,
  grinds: [],
});

const addLineItemToProduct = (
  existing: BatchSummaryProduct | undefined,
  lineItem: LineItem,
  data: { quantity: number; weightInGrams: number; isErrored: boolean }
): BatchSummaryProduct => {
  const product = existing ?? emptyProduct(lineItem.productTitle);
  const { quantity, weightInGrams, isErrored } = data;

  return {
    ...product,
    totalUnits: product.totalUnits + quantity,
    totalWeight: product.totalWeight + weightInGrams,
    fulfilledUnits: product.fulfilledUnits + (isErrored ? 0 : quantity),
    fulfilledWeight: product.fulfilledWeight + (isErrored ? 0 : weightInGrams),
    errorUnits: product.errorUnits + (isErrored ? quantity : 0),
    errorWeight: product.errorWeight + (isErrored ? weightInGrams : 0),
    grinds: rollUpGrind(product.grinds, lineItem.grindType, data),
  };
};

// Mirrors client/src/components/Batch/BatchSummary/BatchSummary.tsx
// `getBatchProducts`: rolls purchase-order line items up into a per-product
// pick list, broken down by grind type. Keeps fulfilled / error tallies per
// product and per grind so a future view can highlight errored quantities.
export const getBatchSummaryProducts = (batch?: LabelPrintBatchDetail | null): BatchSummaryProduct[] => {
  if (!batch?.purchaseOrders?.length) return [];

  const productsByTitle = new Map<string, BatchSummaryProduct>();

  for (const purchaseOrder of batch.purchaseOrders) {
    const isErrored = ERROR_STATUSES.has(`${purchaseOrder.orderStatus}`);
    for (const lineItem of purchaseOrder.lineItems ?? []) {
      const title = lineItem.productTitle ?? "";
      const weightInGrams = Number(lineItem.lineItemWeightInGrams ?? 0);
      const quantity = Number(lineItem.quantity ?? 0);

      const nextProduct = addLineItemToProduct(productsByTitle.get(title), lineItem, { quantity, weightInGrams, isErrored });

      productsByTitle.set(title, nextProduct);
    }
  }

  return Array.from(productsByTitle.values());
};

const rollUpGrind = (
  grinds: BatchSummaryGrind[],
  grindType: string | undefined,
  data: { quantity: number; weightInGrams: number; isErrored: boolean }
): BatchSummaryGrind[] => {
  if (!grindType) return grinds;

  const existing = grinds.find((g) => g.grindType === grindType);
  const base: BatchSummaryGrind = existing ?? {
    grindType,
    totalUnits: 0,
    totalWeight: 0,
    fulfilledUnits: 0,
    fulfilledWeight: 0,
    errorUnits: 0,
    errorWeight: 0,
  };

  const next: BatchSummaryGrind = {
    ...base,
    totalUnits: base.totalUnits + data.quantity,
    totalWeight: base.totalWeight + data.weightInGrams,
    fulfilledUnits: base.fulfilledUnits + (data.isErrored ? 0 : data.quantity),
    fulfilledWeight: base.fulfilledWeight + (data.isErrored ? 0 : data.weightInGrams),
    errorUnits: base.errorUnits + (data.isErrored ? data.quantity : 0),
    errorWeight: base.errorWeight + (data.isErrored ? data.weightInGrams : 0),
  };

  return existing ? grinds.map((g) => (g.grindType === grindType ? next : g)) : [...grinds, next];
};
